import { useCallback, useState } from "react";

import { Heading } from "@magnetic/heading";
import { Input } from "@magnetic/input";
import { Toggle } from "@magnetic/toggle";

import { Modal } from "src/components";
import { Button } from "src/components/buttons";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { LabelFieldContainer } from "src/components/labelFieldContainer";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";
import { Brig } from "src/gen/rest/brig/BrigService";
import { UpdateUser, UpdateUserRequest } from "src/gen/schema/brig/bapi";
import { UserRole } from "src/gen/schema/models/auth";
import { User as Bot } from "src/gen/schema/models/user";

interface EditBotModalProps {
  readonly bot: Bot;
  readonly show: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export const EditBotModal = ({
  bot,
  show,
  onClose,
  onSuccess
}: EditBotModalProps) => {
  const [error, setError] = useState<ApiError | undefined>();
  const [enabled, setEnabled] = useState<boolean>(bot.enabled ?? true);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSave = useCallback(() => {
    setError(undefined);
    setLoading(true);

    const updateUser = UpdateUser.create({
      role: UserRole.READ_ONLY as number,
      enabled
    });

    Brig.updateUser(
      UpdateUserRequest.create({
        userId: bot.id,
        user: updateUser
      })
    )
      .then(() => {
        onSuccess();
        onClose();
      })
      .catch(setError)
      .finally(() => {
        setLoading(false);
      });
  }, [bot.id, enabled, onClose, onSuccess]);

  const handleEnableChange = useCallback(() => {
    setEnabled((val) => !val);
  }, []);

  return (
    <Modal className="edit-bot-modal" isOpen={show} size="lg">
      <VFlex>
        <HFlex
          align="center"
          gap="sm"
          margin={{
            bottom: 16
          }}
        >
          <Heading as="h2" size="primary">
            Edit bot user
          </Heading>
        </HFlex>
        <ErrorNotification error={error} />
        <Input
          label="Name"
          name="name"
          value={bot?.email}
          disabled={true}
          required={true}
        />
        <LabelFieldContainer label="Status" name="status" size="md">
          <Toggle name="status" onClick={handleEnableChange} checked={enabled}>
            <span id="status-desc">{enabled ? "Enabled" : "Disabled"}</span>
          </Toggle>
        </LabelFieldContainer>
        <HFlex justify="flex-end">
          <Button.Group>
            <Button
              kind="tertiary"
              name="modal-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button name="modal-save" onClick={handleSave} disabled={loading}>
              Save
            </Button>
          </Button.Group>
        </HFlex>
      </VFlex>
    </Modal>
  );
};
